import {
  GROUP_ORDER_SUBSIDY_TYPE_FIXED,
  GROUP_ORDER_SUBSIDY_TYPE_PERCENT,
  GROUP_ORDER_SUBSIDY_TYPE_REVERSE,
  GROUP_ORDER_SUBSIDY_TYPE_HUNGRY_WALLET,
  GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE,
} from '../constants'
import {
  Availability,
  VendorGroup,
  PreviewMenu,
  PreviewMenuItem,
  UniversalMenuPreviewMenu,
  UniversalMenuPreviewMenuItem,
  UniversalMenuPreviewFilters,
} from '@types'
import { parseMilitaryTimeToAMPM } from '@utils'

type CSVRow = Record<string, string | number | boolean>

export const vendorGroupsToCSV = (vendorGroups: VendorGroup[]): CSVRow[] => {
  return vendorGroups.flatMap((vg) => menusToCSV(vg.menus))
}

const menusToCSV = (menus: PreviewMenu[]): CSVRow[] => {
  if (!menus || menus.length == 0) {
    return []
  }
  const filteredMenus = menus.filter((menu) => menu.availability.available)

  return filteredMenus.flatMap((menu) => menuToCSVRow(menu))
}

const menuToCSVRow = (menu: PreviewMenu): CSVRow[] => {
  const filteredItems = menu.allMenuItems.filter(
    (item) => item.availability.available,
  )

  return filteredItems.map((item) => itemToCSVRow(item, menu))
}

const itemToCSVRow = (item: PreviewMenuItem, menu: PreviewMenu): CSVRow => {
  let earliestPickup = 'Not Set'
  let maxItemsPerDay = 'Not Set'
  if (menu.chef.chefProfile.earliestGroupOrderPickupTime) {
    earliestPickup = parseMilitaryTimeToAMPM(
      menu.chef.chefProfile.earliestGroupOrderPickupTime,
    )
  }
  if (menu.chef.chefProfile.maxGroupOrderItems) {
    maxItemsPerDay = menu.chef.chefProfile.maxGroupOrderItems.toString()
  }

  return {
    'Vendor Name': menu.chef.fullName,
    'Vendor Earliest Pickup': earliestPickup,
    'Vendor Max Items Per Day': maxItemsPerDay,
    'Menu Name': menu.name,
    'Is Menu Universal': menu.isUniversal ? 'Yes' : 'No',
    'Menu Avg Margin': `${menu.margin.toFixed(2)}%`,
    'Item Name': item.name,
    'Meal Type': item.mealType,
    'Has Mods': item.hasMods ? 'Yes' : 'No',
    'Customizable Alternative Name': item.customizedAlternative
      ? item.customizedAlternative
      : 'N/A',
    'Dietary Tags': item.tagsDietaryPrefList,
    'Vendor Price': `$${item.chefPrice.toFixed(2)}`,
    'Client Price': `$${item.price.toFixed(2)}`,
    Margin: `${item.margin.toFixed(2)}%`,
    'Subsidy Covered': `$${item.subsidyCovered.toFixed(2)}`,
    'Employee Pays': `$${item.employeePays.toFixed(2)}`,
  }
}

type SubsidyBreakdown = {
  subsidyCovered: number
  employeePays: number
}

const clamp = (n: number, lo: number, hi: number) =>
  Math.min(hi, Math.max(lo, n))

export const calcSubsidyBreakdown = (
  clientPrice: number,
  filters: UniversalMenuPreviewFilters,
): SubsidyBreakdown => {
  const price = Math.max(0, Number(clientPrice) || 0)
  const t = filters.subsidyType
  const amt = filters.subsidyAmt ?? 0

  let subsidyCovered = 0
  let employeePays = price
  switch (t) {
    case GROUP_ORDER_SUBSIDY_TYPE_HUNGRY_WALLET:
    case GROUP_ORDER_SUBSIDY_TYPE_FIXED: {
      subsidyCovered = clamp(amt, 0, price)
      employeePays = price - subsidyCovered
      break
    }
    case GROUP_ORDER_SUBSIDY_TYPE_PERCENT: {
      const pct = (clamp(amt, 0, 100) / 100).toFixed(2)
      subsidyCovered = Number((price * Number(pct)).toFixed(2))
      employeePays = price - subsidyCovered
      break
    }
    case GROUP_ORDER_SUBSIDY_TYPE_FLAT_PRICE:
    case GROUP_ORDER_SUBSIDY_TYPE_REVERSE: {
      const flat = clamp(amt, 0, price)
      employeePays = flat
      subsidyCovered = price - flat
      break
    }
    default: {
      subsidyCovered = 0
      employeePays = price
    }
  }

  return {
    subsidyCovered,
    employeePays,
  }
}

export const disabledClasses = (
  disabled: boolean,
  base: string,
  whenDisabled: string,
) => (disabled ? `${base} ${whenDisabled}` : base)

const normalizeText = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

export const menuOrItemsMatchSearch = (
  chefName: string,
  menuName: string,
  items: Array<{ name: string }>,
  search: string,
): boolean => {
  const normalizedSearch = normalizeText(search)
  if (!normalizedSearch) {
    return true
  }
  if (normalizeText(chefName).includes(normalizedSearch)) {
    return true
  }
  if (normalizeText(menuName).includes(normalizedSearch)) {
    return true
  }
  for (const it of items) {
    if (normalizeText(it.name).includes(normalizedSearch)) {
      return true
    }
  }

  return false
}

export const decorateItem = (
  it: UniversalMenuPreviewMenuItem,
  f: UniversalMenuPreviewFilters,
): Omit<PreviewMenuItem, 'availability'> => {
  const clientPrice = it.price
  const { subsidyCovered, employeePays } = calcSubsidyBreakdown(it.price, f)
  const margin = Number(
    (((clientPrice - it.chefPrice) / clientPrice) * 100).toFixed(2),
  )

  return {
    ...it,
    subsidyCovered,
    employeePays,
    margin,
  }
}

export const computeMenuAvailability = (
  menu: UniversalMenuPreviewMenu,
  f: UniversalMenuPreviewFilters,
  blacklistedVendorIds: Set<string>,
): Availability => {
  const reasons: string[] = []

  if (menu.chef?.id && blacklistedVendorIds.has(menu.chef.id)) {
    reasons.push('Vendor blacklisted (universal settings)')
  }

  return { available: reasons.length === 0 }
}

export const computeItemAvailbility = (
  item: UniversalMenuPreviewMenuItem,
  blacklistedItemIds: Set<string>,
  filters: UniversalMenuPreviewFilters,
): Availability => {
  const reasons = []
  if (item.id && blacklistedItemIds.has(item.id)) {
    reasons.push('Item blacklisted')
  }
  if (!filters.enableSides && item.mealType == 'Side') {
    reasons.push('Sides disabled')
  }
  if (!filters.enableMods && item.hasMods) {
    reasons.push('Modifications Disabled')
  }
  if (filters.maxPriceLimit && item.price > filters.maxPriceLimit) {
    reasons.push('Item Price is greater than max price limit')
  }
  if (
    filters.flatPriceMarginMin != null &&
    item.margin < filters.flatPriceMarginMin
  ) {
    reasons.push('Item margin is less than flat price margin min')
  }

  return { available: reasons.length === 0 }
}

export const groupByVendor = (menus: PreviewMenu[]): VendorGroup[] => {
  const map = new Map<string, VendorGroup>()
  for (const m of menus) {
    const vid = m.chef?.id ?? 'unknown'
    const vname = m.chef?.fullName ?? 'Unknown Vendor'
    const maxItemsPerDay = m.chef.chefProfile.maxGroupOrderItems || undefined
    const earliestGroupOrderPickupTime =
      m.chef.chefProfile.earliestGroupOrderPickupTime || undefined
    if (!map.has(vid)) {
      map.set(vid, {
        vendorId: vid,
        vendorName: vname,
        menus: [],
        maxItemsPerDay: maxItemsPerDay,
        earliestPickup: earliestGroupOrderPickupTime,
      })
    }
    const vendorGroup = map.get(vid)
    if (vendorGroup) {
      vendorGroup.menus.push(m)
    }
  }

  return Array.from(map.values()).sort((a, b) =>
    a.vendorName.localeCompare(b.vendorName),
  )
}
